import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useState } from "react";
import SnackbarMessage from "../components/common/SnackbarMessage";

const useFetchObjects = (queryKey, endpoint, token, options = {}) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
    title: "",
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchObjects = async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
      };

      // Only add Authorization header if token exists
      if (token) {
        headers.Authorization = `Token ${token}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${endpoint}`,
        {
          headers,
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle invalid token
        if (response.status === 401 || 
            errorData?.detail === "Invalid token.") {
          localStorage.removeItem('token');
          localStorage.clear();
          window.location.reload();
          return;
        }
        
        throw new Error("Network response was not ok");
      }

      return response.json();
    } catch (error) {
      // Handle axios-like error structure for consistency
      if (error.response?.status === 401 || 
          error.response?.data?.detail === "Invalid token.") {
        localStorage.removeItem('token');
        localStorage.clear();
        window.location.reload();
        return;
      }
      throw error;
    }
  };

  const { data, isLoading, isError, isSuccess, error, refetch } = useQuery({
    queryKey: queryKey,
    queryFn: fetchObjects,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
    refetchInterval: options.refetchInterval ?? false,
    retry: options.retry ?? 1,
    retryDelay: options.retryDelay ?? 1000,
    enabled: options.enabled ?? true,
    onError: (error) => {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
        title: "Error",
      });
    },
  });

  return {
    data,
    isLoading,
    isError,
    isSuccess,
    error,
    refetch,
    SnackbarMessage: (
      <SnackbarMessage
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
        title={snackbar.title}
      />
    ),
  };
};

export default useFetchObjects;
